import { useEffect, useState } from "react";
import { useCacheVersion } from "@/hooks/useCacheVersion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CACHE_PREFIX = "kw_portfolio_";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h — covers rarely change

const readCache = (slug, version) => {
  if (typeof window === "undefined" || !version) return null;
  try {
    const raw = window.localStorage.getItem(`${CACHE_PREFIX}${slug}_${version}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (Date.now() - (parsed.t || 0)) > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCache = (slug, version, data) => {
  if (typeof window === "undefined" || !version) return;
  try {
    window.localStorage.setItem(
      `${CACHE_PREFIX}${slug}_${version}`,
      JSON.stringify({ t: Date.now(), data }),
    );
  } catch {
    /* quota / private mode — silent */
  }
};

/**
 * Loads admin-uploaded portfolio media for a given service slug.
 *
 * - When a cached COVER exists we hydrate synchronously so the hero photo
 *   appears INSTANTLY (no flash of the static fallback). A background
 *   revalidation fetch then refreshes the cache if the admin changed
 *   anything.
 * - When no cached cover is present (first visit OR previous cache stored
 *   `cover: null` from a time when the admin hadn't uploaded one yet) we
 *   intentionally keep `loading: true` so the consumer can render an empty
 *   hero rather than the static Pexels default — eliminating the visible
 *   "default → admin photo" swap.
 *
 * Returns: { media, images, cover, loading }
 */
export function usePortfolioImages(slug) {
  const version = useCacheVersion();
  const cached = slug ? readCache(slug, version) : null;
  const hasCachedCover = !!cached?.cover;
  const [data, setData] = useState(() => ({
    media: cached?.media || [],
    images: cached?.images || [],
    cover: cached?.cover || null,
    // Stay in loading state until we KNOW whether the admin has a cover.
    // Once a real cover is in cache, we can trust it instantly.
    loading: !hasCachedCover,
  }));

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      queueMicrotask(() =>
        setData({ media: [], images: [], cover: null, loading: false }),
      );
      return;
    }
    // Re-hydrate cache when slug or cache-version changes — only flip
    // loading=false when the cache actually has a cover (otherwise wait
    // for the fresh fetch so we don't flash the static default).
    const hit = readCache(slug, version);
    if (hit) {
      setData({ ...hit, loading: !hit.cover });
    }
    const run = async () => {
      try {
        const r = await fetch(`${BACKEND}/api/portfolio/${slug}`);
        if (!r.ok) throw new Error("fetch failed");
        const items = await r.json();
        const list = Array.isArray(items) ? items : [];
        const media = list.map((x) => ({
          id: x.id,
          url: `${BACKEND}${x.url}`,
          thumb_url: x.thumb_url ? `${BACKEND}${x.thumb_url}` : null,
          media_type: x.media_type || "image",
          mime_type: x.mime_type || null,
          is_cover: !!x.is_cover,
          orientation: x.orientation === "vertical" ? "vertical" : "horizontal",
          caption: x.caption || null,
        }));
        const imagesOnly = media
          .filter((m) => m.media_type !== "video")
          .map((m) => m.url);
        const cover = media.find((m) => m.is_cover) || null;
        const next = {
          media,
          images: imagesOnly,
          cover: cover ? cover.url : null,
          loading: false,
        };
        if (!cancelled) {
          setData(next);
          writeCache(slug, version, next);
        }
      } catch {
        if (!cancelled) {
          setData((prev) => ({ ...prev, loading: false }));
        }
      }
    };
    queueMicrotask(() => run());
    return () => {
      cancelled = true;
    };
  }, [slug, version]);

  return data;
}
